import { Chip, ChipRow } from "../lib/Chip.tsx";
import { EmptyState } from "../lib/EmptyState.tsx";
import { RevisionsPanel } from "../revisions/RevisionsPanel.tsx";
import { InventoryRow } from "./InventoryRow.tsx";
import { SellWizard } from "./SellWizard.tsx";
import { GALLERY_COPY } from "./gallery.constants.ts";
import { useInventoryController } from "./useInventoryController.ts";
import { useSellWizardController } from "./useSellWizardController.ts";
import "./gallery.css";

/**
 * The works consigned to this gallery, and the one act it may perform on them
 * today.
 *
 * The design's front page is a list of green-coded works each with a **Sell**
 * button, and that button now opens the five-step wizard it was drawn for. Two
 * acts live here and they are different shapes: answering an offer is settled on
 * the row, because a gallery deciding what to take wants to see all of them at
 * once; selling opens a dialog, because it carries a price, a buyer and terms
 * that bind for years.
 *
 * WHETHER SELL IS LIVE IS THE SERVER'S ANSWER, never this file's. The screen is
 * passed `capabilities.transferWork` — `exists (select 1 from
 * ondo.sellable_work_ids())`, which is a live accepted consignment held at
 * `editor` or above — and disables rather than hides the control when it is
 * false, so a director meets a refusal they can read instead of a control that
 * silently is not there.
 *
 * It carries no title of its own: the portal header above it does, and two
 * headings saying "Inventory" one under the other is what a rail makes redundant.
 */
export function Inventory({
  workBase,
  canSell,
  canAnswer,
}: {
  workBase: string;
  /** `capabilities.transferWork`. The server's answer, passed rather than guessed. */
  canSell: boolean;
  /**
   * Editor on this gallery. `ondo.answer_consignment()` requires it.
   *
   * Separate from `canSell`, which is the server's answer about a WORK — a
   * live accepted consignment. Accepting an offer is what creates that, so a
   * seat that cannot answer would see every offer and be refused on both
   * buttons.
   */
  canAnswer: boolean;
}) {
  const { view, chooseArtist, answer, answering, selling, openSell, closeSell, soldOne } =
    useInventoryController(workBase);
  const sell = useSellWizardController(selling, soldOne, closeSell);

  if (view.kind === "loading") {
    return (
      <p className="gallery__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="gallery__note gallery__note--error" role="alert">
        <h2 className="gallery__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  if (view.kind === "empty") {
    return (
      <EmptyState className="gallery__empty" lead={view.message} detail={view.detail} />
    );
  }

  return (
    <>
      <p className="gallery__summary">{view.summary}</p>

      <section className="gallery__lead">
        <p>
          <strong>{GALLERY_COPY.lead}</strong> {GALLERY_COPY.leadAction}
        </p>
      </section>

      {view.artists.length > 0 && (
        <ChipRow label={GALLERY_COPY.artistFilter}>
          <Chip pressed={view.artist === ""} onClick={() => chooseArtist("")}>
            {GALLERY_COPY.allArtists}
          </Chip>
          {view.artists.map((each) => (
            <Chip
              key={each}
              pressed={view.artist === each}
              onClick={() => chooseArtist(each)}
            >
              {each}
            </Chip>
          ))}
        </ChipRow>
      )}

      {/* OFFERS FIRST, and separated by a heading rather than a badge on a row.
          A gallery reading this screen is doing two different things — deciding
          what to take, and selling what it has — and a list that interleaved
          them would make the first invisible among the second. */}
      {view.offers.length > 0 && (
        <section className="gallery__offers">
          <h2 className="gallery__section-title">{GALLERY_COPY.offersTitle}</h2>
          <p className="gallery__section-note">{GALLERY_COPY.offersDetail}</p>

          <ul className="gallery__rows">
            {view.offers.map((row) => (
              <InventoryRow
                key={row.consignmentId}
                row={row}
                action={
                  canAnswer ? (
                    <>
                      <button
                        type="button"
                        className="gallery__accept"
                        disabled={answering === row.consignmentId}
                        onClick={() => answer(row.consignmentId, true)}
                      >
                        {GALLERY_COPY.accept}
                      </button>
                      <button
                        type="button"
                        className="gallery__decline"
                        disabled={answering === row.consignmentId}
                        onClick={() => answer(row.consignmentId, false)}
                      >
                        {GALLERY_COPY.decline}
                      </button>
                    </>
                  ) : (
                    // The offer is still shown — knowing what has been offered
                    // is the least a cataloguer needs — but answering it binds
                    // the gallery to terms, which is not their seat's to do.
                    <p className="gallery__note gallery__note--quiet">
                      {GALLERY_COPY.answerNeedsEditor}
                    </p>
                  )
                }
              />
            ))}
          </ul>
        </section>
      )}

      {view.filteredOut ? (
        <p className="gallery__note">{GALLERY_COPY.noMatches}</p>
      ) : (
        <ul className="gallery__rows">
          {view.held.map((row) => (
            <InventoryRow
              key={row.consignmentId}
              row={row}
              /* THE GALLERY'S HALF OF OND-101, under the row rather than beside
                 it. `canAnswer` and not `canSell`: proposing new terms commits
                 the gallery to what it may sell for, which is the editor rung
                 that answering an offer takes — where `canSell` reports
                 `capabilities.transferWork`, a different question about a
                 different door. */
              footer={<RevisionsPanel consignmentId={row.consignmentId} canAct={canAnswer} />}
              action={
                /* DISABLED RATHER THAN HIDDEN, which is the choice
                   `capabilities.transferWork` exists to make: the server says no
                   and the client shows the refusal, instead of hiding a control
                   and leaving a director to wonder whether the feature or their
                   data is missing. `title` says which. */
                <button
                  type="button"
                  className="gallery__sell"
                  disabled={!canSell}
                  title={canSell ? undefined : GALLERY_COPY.sellNotYet}
                  onClick={() => openSell(row.consignmentId)}
                >
                  {GALLERY_COPY.sell} <span aria-hidden="true">→</span>
                </button>
              }
            />
          ))}
        </ul>
      )}

      {/* MOUNTED EVEN WHEN SHUT. Closing by unmounting would skip the dialog's
          own close(), and it is close() that returns focus to the Sell button
          that opened it — the argument AddHistoryDialog makes first. */}
      <SellWizard controller={sell} />
    </>
  );
}
